import { useEffect, useState, type FormEvent } from 'react'
import { services } from '../data/services'

interface ApiSuccess {
  ok: true
  reference: string
  message: string
  calendarHoldCreated?: boolean
  summary: Record<string, string | number | boolean>
}

interface ApiFailure {
  ok: false
  message: string
  errors?: Record<string, string>
}

type ApiResponse = ApiSuccess | ApiFailure

interface AvailabilitySuccess {
  ok: true
  slots: string[]
  timeZone: string
  slotMinutes: number
  businessHours: { start: string; end: string }
}

interface AvailabilityFailure {
  ok: false
  message: string
}

type AvailabilityResponse = AvailabilitySuccess | AvailabilityFailure

const fallbackTimeWindows = [
  'Morning (8 AM–12 PM)',
  'Afternoon (12–5 PM)',
  'Flexible',
]

const formatCentralTime = (time: string) => {
  const [hour, minute] = time.split(':').map(Number)
  const suffix = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12
  return `${displayHour}:${String(minute).padStart(2, '0')} ${suffix}`
}

const initialState = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  service: 'mobile-notary',
  preferredDate: '',
  preferredTime: '',
  city: '',
  signers: '1',
  documents: '1',
  stamps: '1',
  notaryPreference: 'Traditional / in person',
  urgency: 'Standard',
  courierNeeded: 'No',
  notes: '',
  website: '',
}

export function RequestForm() {
  const [form, setForm] = useState(initialState)
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [response, setResponse] = useState<ApiResponse | null>(null)
  const [availabilityStatus, setAvailabilityStatus] = useState<'idle' | 'loading' | 'live' | 'fallback'>('idle')
  const [availability, setAvailability] = useState<AvailabilitySuccess | null>(null)
  const [availabilityMessage, setAvailabilityMessage] = useState('')

  useEffect(() => {
    if (!form.preferredDate) return

    const controller = new AbortController()

    const loadAvailability = async () => {
      try {
        const request = await fetch(`/api/availability?date=${encodeURIComponent(form.preferredDate)}`, {
          signal: controller.signal,
        })
        const payload = (await request.json()) as AvailabilityResponse
        if (request.ok && payload.ok) {
          setAvailability(payload)
          setAvailabilityStatus('live')
          return
        }
        setAvailabilityMessage('message' in payload ? payload.message : 'Live calendar availability is unavailable.')
        setAvailabilityStatus('fallback')
      } catch (error) {
        if ((error as Error).name === 'AbortError') return
        setAvailabilityMessage('Live calendar availability is unavailable.')
        setAvailabilityStatus('fallback')
      }
    }

    void loadAvailability()
    return () => controller.abort()
  }, [form.preferredDate])

  const update = (name: keyof typeof form, value: string) => {
    if (name === 'preferredDate') {
      setAvailabilityStatus(value ? 'loading' : 'idle')
      setAvailability(null)
      setAvailabilityMessage('')
      setForm((current) => ({ ...current, preferredDate: value, preferredTime: '' }))
      return
    }
    setForm((current) => ({ ...current, [name]: value }))
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setStatus('submitting')
    setResponse(null)

    try {
      const request = await fetch('/api/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestType: 'appointment', ...form }),
      })
      const payload = (await request.json()) as ApiResponse
      setResponse(payload)
      setStatus(request.ok && payload.ok ? 'success' : 'error')
    } catch {
      setResponse({
        ok: false,
        message: 'The request could not be sent. Please try again in a moment.',
      })
      setStatus('error')
    }
  }

  if (status === 'success' && response?.ok) {
    return (
      <section className="request-confirmation" aria-labelledby="confirmation-title">
        <span className="status-mark" aria-hidden="true">✓</span>
        <span className="eyebrow">Request received</span>
        <h2 id="confirmation-title">Thank you, {form.firstName}.</h2>
        <p>{response.message}</p>
        <p className="reference">Request reference: <strong>{response.reference}</strong></p>
        <div className="summary-card">
          <h3>Request summary</h3>
          <dl>
            <div><dt>Service</dt><dd>{services.find((service) => service.id === form.service)?.name ?? form.service}</dd></div>
            <div><dt>Preferred date</dt><dd>{form.preferredDate}</dd></div>
            <div><dt>Time window</dt><dd>{form.preferredTime}</dd></div>
            <div><dt>Location</dt><dd>{form.city}</dd></div>
            <div><dt>Signers / stamps</dt><dd>{form.signers} / {form.stamps}</dd></div>
          </dl>
        </div>
        <p>Michael will confirm availability and final pricing before the appointment is scheduled.</p>
        <button
          className="button button-secondary"
          type="button"
          onClick={() => {
            setForm(initialState)
            setStatus('idle')
            setResponse(null)
            setAvailabilityStatus('idle')
            setAvailability(null)
            setAvailabilityMessage('')
          }}
        >
          Start another request
        </button>
      </section>
    )
  }

  return (
    <form className="request-form" onSubmit={submit} noValidate={false}>
      <div className="form-section">
        <div className="form-section-heading">
          <span>01</span>
          <div><h2>Your information</h2><p>How Michael can reach you about this request.</p></div>
        </div>
        <div className="form-grid two-columns">
          <label>First name <span aria-hidden="true">*</span><input required autoComplete="given-name" value={form.firstName} onChange={(event) => update('firstName', event.target.value)} maxLength={80} /></label>
          <label>Last name <span aria-hidden="true">*</span><input required autoComplete="family-name" value={form.lastName} onChange={(event) => update('lastName', event.target.value)} maxLength={80} /></label>
          <label>Phone <span aria-hidden="true">*</span><input required type="tel" autoComplete="tel" value={form.phone} onChange={(event) => update('phone', event.target.value)} maxLength={30} /></label>
          <label>Email <span aria-hidden="true">*</span><input required type="email" autoComplete="email" value={form.email} onChange={(event) => update('email', event.target.value)} maxLength={160} /></label>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-heading">
          <span>02</span>
          <div><h2>Service details</h2><p>A practical overview—no confidential documents are needed.</p></div>
        </div>
        <div className="form-grid two-columns">
          <label>Service requested <span aria-hidden="true">*</span><select required value={form.service} onChange={(event) => update('service', event.target.value)}>{services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}</select></label>
          <label>City / meeting location <span aria-hidden="true">*</span><input required autoComplete="address-level2" value={form.city} onChange={(event) => update('city', event.target.value)} maxLength={120} placeholder="City or general location" /></label>
          <label>Number of signers <input type="number" min="0" max="50" value={form.signers} onChange={(event) => update('signers', event.target.value)} /></label>
          <label>Approximate documents <input type="number" min="0" max="200" value={form.documents} onChange={(event) => update('documents', event.target.value)} /></label>
          <label>Approximate notarizations / stamps <input type="number" min="0" max="200" value={form.stamps} onChange={(event) => update('stamps', event.target.value)} /></label>
          <label>Notary preference <select value={form.notaryPreference} onChange={(event) => update('notaryPreference', event.target.value)}><option>Traditional / in person</option><option>Electronic / online</option><option>Not sure</option></select></label>
          <label>Courier needed? <select value={form.courierNeeded} onChange={(event) => update('courierNeeded', event.target.value)}><option>No</option><option>Yes</option><option>Not sure</option></select></label>
          <label>Urgency <select value={form.urgency} onChange={(event) => update('urgency', event.target.value)}><option>Standard</option><option>Within 48 hours</option><option>Same day, if available</option></select></label>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-heading">
          <span>03</span>
          <div><h2>Preferred timing</h2><p>This is a request, not an automatic booking.</p></div>
        </div>
        <div className="form-grid two-columns">
          <label>Preferred date <span aria-hidden="true">*</span><input required type="date" min={new Date().toISOString().slice(0, 10)} value={form.preferredDate} onChange={(event) => update('preferredDate', event.target.value)} /></label>
          <label>
            {availabilityStatus === 'live' ? 'Available time (Central)' : 'Preferred time window'} <span aria-hidden="true">*</span>
            <select
              required
              value={form.preferredTime}
              onChange={(event) => update('preferredTime', event.target.value)}
              disabled={availabilityStatus === 'idle' || availabilityStatus === 'loading' || (availabilityStatus === 'live' && !availability?.slots.length)}
            >
              <option value="">
                {availabilityStatus === 'idle'
                  ? 'Choose a date first'
                  : availabilityStatus === 'loading'
                    ? 'Checking Michael’s calendar…'
                    : availabilityStatus === 'live' && !availability?.slots.length
                      ? 'No open times on this date'
                      : 'Choose a time'}
              </option>
              {availabilityStatus === 'live'
                ? availability?.slots.map((slot) => <option key={slot} value={slot}>{formatCentralTime(slot)}</option>)
                : availabilityStatus === 'fallback'
                  ? fallbackTimeWindows.map((window) => <option key={window}>{window}</option>)
                  : null}
            </select>
          </label>
        </div>
        {availabilityStatus === 'live' && availability && (
          <p className="availability-note" role="status">
            Live availability from Michael’s calendar, shown in Central Time between 8:00 AM and 5:00 PM. Selecting a time requests a tentative {availability.slotMinutes}-minute hold; Michael still confirms the appointment and final price.
          </p>
        )}
        {availabilityStatus === 'fallback' && (
          <p className="availability-note availability-note-warning" role="status">
            {availabilityMessage} You can still choose a preferred window, and Michael will confirm the exact time.
          </p>
        )}
        <label>Additional notes <textarea rows={5} value={form.notes} onChange={(event) => update('notes', event.target.value)} maxLength={2000} placeholder="Share practical scheduling details or questions. Do not paste document contents." /></label>
      </div>

      <div className="honeypot" aria-hidden="true">
        <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={(event) => update('website', event.target.value)} /></label>
      </div>

      <div className="privacy-callout">
        <strong>Protect your private information.</strong>
        <p>Please do not submit Social Security numbers, identification images, banking information, medical records, or confidential documents through this form.</p>
      </div>

      {status === 'error' && response && !response.ok && (
        <div className="form-error" role="alert">
          <strong>We could not submit your request.</strong>
          <p>{response.message}</p>
        </div>
      )}

      <button className="button button-large" type="submit" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Sending request…' : 'Submit appointment request'}
      </button>
      <p className="fine-print">Submitting creates a request only. Michael will confirm availability and final pricing before scheduling.</p>
    </form>
  )
}
